// AddItemPicker — search SRD equipment & magic items and add one to a character's
// inventory. A compact modal used from the character drawer during play. When the
// chosen item is a generic "+N weapon/armor/shield" template, it prompts for the
// underlying base item so the enchantment applies on top of real stats.
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import type { CompendiumKind, CompendiumResult } from "../lib/types";
import { titleizeSlug } from "../lib/format";
import { Modal, Spinner } from "./ui";

const SOURCES: { kind: CompendiumKind; label: string }[] = [
  { kind: "equipment", label: "Equipment" },
  { kind: "magic-items", label: "Magic Items" },
];

// Generic +N templates that need a concrete base item chosen.
const PLUS_TEMPLATES: Record<string, "weapon" | "armor" | "shield"> = {
  "weapon-1": "weapon", "weapon-2": "weapon", "weapon-3": "weapon",
  "armor-1": "armor", "armor-2": "armor", "armor-3": "armor",
  "shield-1": "shield", "shield-2": "shield", "shield-3": "shield",
};

export function AddItemPicker({
  onClose,
  onPick,
}: {
  onClose: () => void;
  onPick: (index: string, name: string, base?: string) => void;
}) {
  const [kind, setKind] = useState<CompendiumKind>("equipment");
  const [q, setQ] = useState("");
  const [results, setResults] = useState<CompendiumResult[]>([]);
  const [loading, setLoading] = useState(false);

  // When a +N template is chosen, we collect its base item here.
  const [pending, setPending] = useState<{ index: string; name: string; slot: string } | null>(null);
  const [baseResults, setBaseResults] = useState<CompendiumResult[]>([]);
  const [baseQ, setBaseQ] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const t = window.setTimeout(() => {
      api.compendium(kind, { q, limit: 60 })
        .then((r) => alive && setResults(r.results))
        .catch(() => alive && setResults([]))
        .finally(() => alive && setLoading(false));
    }, 180);
    return () => { alive = false; window.clearTimeout(t); };
  }, [kind, q]);

  // Load candidate base items for the pending +N template (filter to its slot).
  useEffect(() => {
    if (!pending) return;
    let alive = true;
    const wantArmorCat = pending.slot === "shield" ? "shield" : pending.slot === "armor" ? "armor" : null;
    api.compendium("equipment", { q: baseQ, limit: 120 }).then((r) => {
      if (!alive) return;
      // Heuristic filter by slot using the name (the list endpoint is name-only).
      const filtered = r.results.filter((it) => {
        const n = it.name.toLowerCase();
        if (pending.slot === "weapon") return !/(armor|mail|plate|shield|leather|robe)/.test(n);
        if (pending.slot === "shield") return n.includes("shield");
        return /(armor|mail|plate|leather|breastplate|hide|chain|scale|splint|padded|ring mail)/.test(n);
      });
      setBaseResults(filtered);
    }).catch(() => alive && setBaseResults([]));
    return () => { alive = false; };
  }, [pending, baseQ]);

  function choose(r: CompendiumResult) {
    const slot = PLUS_TEMPLATES[r.index];
    if (kind === "magic-items" && slot) {
      setPending({ index: r.index, name: r.name, slot });
    } else {
      onPick(r.index, r.name);
    }
  }

  if (pending) {
    return (
      <Modal onClose={onClose}>
        <h2>Choose the base {pending.slot}</h2>
        <div className="modal__sub">
          “{pending.name}” enchants a real {pending.slot}. Pick which one.
        </div>
        <input className="field" autoFocus value={baseQ} onChange={(e) => setBaseQ(e.target.value)}
          placeholder={`Search ${pending.slot}s…`} />
        <div className="comp-results mt12" style={{ maxHeight: "44vh", overflowY: "auto" }}>
          {baseResults.length === 0 && <div className="muted">No matches.</div>}
          {baseResults.map((b) => (
            <div key={b.index} className="comp-item" onClick={() => {
              // Name it like "Longsword, +1"
              const plus = pending.name.match(/\+\s*\d/)?.[0].replace(/\s/g, "") || "+1";
              onPick(pending.index, `${b.name} ${plus}`, b.index);
            }}>
              <span className="ci-name">{b.name}</span>
              <span className="ci-meta">make {pending.name}</span>
            </div>
          ))}
        </div>
        <div className="modal__row">
          <button className="btn btn--ghost" onClick={() => setPending(null)}>← Back</button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose}>
      <h2>Add an Item</h2>
      <div className="modal__sub">Search SRD gear and treasures to add to the pack.</div>

      <div className="comp-tabs">
        {SOURCES.map((s) => (
          <button key={s.kind} className={`comp-tab${kind === s.kind ? " on" : ""}`}
            onClick={() => setKind(s.kind)}>{s.label}</button>
        ))}
      </div>
      <input className="field" autoFocus value={q} onChange={(e) => setQ(e.target.value)}
        placeholder={`Search ${kind === "magic-items" ? "magic items" : "equipment"}…`} />

      <div className="comp-results mt12" style={{ maxHeight: "48vh", overflowY: "auto" }}>
        {loading && <div className="muted flex"><Spinner /> Searching…</div>}
        {!loading && results.length === 0 && <div className="muted">No matches.</div>}
        {results.map((r) => (
          <div key={r.index} className="comp-item" onClick={() => choose(r)}>
            <span className="ci-name">{r.name}</span>
            <span className="ci-meta">
              {PLUS_TEMPLATES[r.index] && kind === "magic-items" ? "pick base →"
                : r.rarity ? titleizeSlug(String(r.rarity)) : "add +"}
            </span>
          </div>
        ))}
      </div>

      <div className="modal__row">
        <button className="btn btn--ghost" onClick={onClose}>Done</button>
      </div>
    </Modal>
  );
}
